import type { SwiperBreakpoints } from '@uefadigital/panenka';
import { PkCarousel } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import type { CarouselItem, CarouselLayout } from '../../types';
import { CardFormat } from '../../types';
import { normalizeText } from '../../utils/utility';

import { Cards } from './Cards';
import { CardsGrouped } from './CardsGrouped';
import { HeaderComponent } from './HeaderComponent';

import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useWindowSize } from '@shared/hooks/useWindowSize';

import * as styles from './styles.module.scss';

const Carousel = ({
  data,
  layout = { cardLayout: { cardFormat: 'wide' }, cardsForColumn: 1 },
}: {
  data: CarouselItem;
  layout?: CarouselLayout;
}) => {
  const { isMobile } = useWindowSize();
  const grouped = useMemo(() => layout?.cardsForColumn > 1, [layout?.cardsForColumn]);
  const BREAKPOINTS: SwiperBreakpoints = layout?.breakpoints ?? {
    0: {
      slidesPerView: layout?.cardLayout.cardFormat === CardFormat.PORTRAIT ? 2.2 : 1.1,
      slidesPerGroup: 1,
      spaceBetween: 12,
    },
    768: {
      slidesPerView: 2.1,
      slidesPerGroup: 2,
      spaceBetween: 24,
    },
    1024: {
      slidesPerView: grouped ? 2.1 : 3.2,
      slidesPerGroup: grouped ? 2 : 3,
      spaceBetween: 24,
    },
    1400: {
      slidesPerView: 4.1,
      slidesPerGroup: 4,
      spaceBetween: 24,
    },
  };

  if (!data?.items?.length) return null;

  return (
    <>
      {isMobile && layout?.staticFirstSlide && (
        <div className='pk-mb--m pk-mb-sm--l'>
          <HeaderComponent
            staticFirstSlide={layout?.staticFirstSlide}
            title={data.title}
            description={data.description}
          />
        </div>
      )}
      <div className={clsx(styles.ueftvCarousel)}>
        <PkCarousel
          key={data.id}
          content=''
          data-tracking-container={normalizeText(data.title)}
          navigationPosition='inside'
          staticFirstSlide={layout?.staticFirstSlide}
          resizeObserver
          breakpoints={BREAKPOINTS}
          className={clsx(layout?.cardLayout.cardFormat, 'js-tracking-container')}
          scaleOnHover={!isMobile && !grouped}
        >
          <HeaderComponent
            staticFirstSlide={layout?.staticFirstSlide}
            title={data.title}
            description={data.description}
          />
          {!grouped && <Cards id={data.id} items={data.items} layout={layout?.cardLayout} />}
          {grouped && (
            <CardsGrouped
              id={data.id}
              items={data.items}
              rowCount={layout?.cardsForColumn}
              layout={layout?.cardLayout}
            />
          )}
        </PkCarousel>
      </div>
    </>
  );
};
export default withErrorBoundary(memo(Carousel));
